import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';

export const CreateAccountPage: React.FC = () => {
  const [name, setName] = useState(''); // Full name for store/club admin
  const [accountType, setAccountType] = useState<'club' | 'store' | ''>(''); // Club or Store selection
  const [storeName, setStoreName] = useState('');
  const [clubName, setClubName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Get marketing website URL from environment variable or use default
  // Default: https://ride-connect-website.vercel.app (testing URL)
  // In production, set VITE_MARKETING_URL environment variable in Vercel
  const getMarketingUrl = (): string => {
    const envUrl = import.meta.env.VITE_MARKETING_URL;
    if (envUrl) {
      // Ensure it has a protocol
      return envUrl.startsWith('http://') || envUrl.startsWith('https://') 
        ? envUrl 
        : `https://${envUrl}`;
    }
    
    // Default to testing URL
    return 'https://ride-connect-website.vercel.app';
  };
  
  const marketingUrl = getMarketingUrl();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!name.trim()) {
        toast.error('Your name is required');
        return;
      }
      if (!accountType) {
        toast.error('Please select whether you are creating a Club or Store account');
        return;
      }
      if (accountType === 'store' && !storeName.trim()) {
        toast.error('Store name is required');
        return;
      }
      if (accountType === 'club' && !clubName.trim()) {
        toast.error('Club name is required');
        return;
      }
      if (!acceptedTerms) {
        toast.error('You must accept the Terms and Conditions');
        return;
      }

      // Step 1: Create Firebase user (handles orphaned users)
      const firebaseResponse = await api.post('/auth/create-firebase-user', { email, password });
      const { firebaseUid } = firebaseResponse.data;
      
      // Step 2: Complete registration by creating Store/Club + User records
      await api.post('/store-auth/register', {
        name: name.trim(),
        email,
        accountType, // 'club' or 'store'
        storeName: accountType === 'store' ? storeName.trim() : undefined,
        clubName: accountType === 'club' ? clubName.trim() : undefined,
        firebaseUid,
        acceptedTerms: true,
        termsAcceptedAt: new Date().toISOString(),
      });
      
      const accountTypeLabel = accountType === 'club' ? 'Club' : 'Store';
      toast.success(`${accountTypeLabel} account created successfully! Please log in to continue.`);
      navigate('/store');
    } catch (error: any) {
      console.error('Account registration error:', error);
      toast.error(error.response?.data?.error || error.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-blue-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Welcome.
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-2 font-semibold">
            You are one step away to building a powerful cycling community
          </p>
          <p className="text-sm text-gray-600 mt-4">
            Create your account to get started
          </p>
        </div>
        
        <form className="mt-8 space-y-6 bg-white p-8 rounded-lg shadow-lg" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Your Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                className="input-field mt-1"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="accountType" className="block text-sm font-medium text-gray-700 mb-2">
                I am creating a:
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setAccountType('club');
                    setStoreName(''); // Clear store name when switching
                  }}
                  className={`px-4 py-3 rounded-lg border-2 font-medium transition-colors ${
                    accountType === 'club'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  🚴 Club
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAccountType('store');
                    setClubName(''); // Clear club name when switching
                  }}
                  className={`px-4 py-3 rounded-lg border-2 font-medium transition-colors ${
                    accountType === 'store'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  🏪 Store
                </button>
              </div>
            </div>

            {accountType === 'store' && (
              <div>
                <label htmlFor="storeName" className="block text-sm font-medium text-gray-700">
                  Store Name
                </label>
                <input
                  id="storeName"
                  name="storeName"
                  type="text"
                  required
                  className="input-field mt-1"
                  placeholder="Enter your store name"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                />
              </div>
            )}

            {accountType === 'club' && (
              <div>
                <label htmlFor="clubName" className="block text-sm font-medium text-gray-700">
                  Club Name
                </label>
                <input
                  id="clubName"
                  name="clubName"
                  type="text"
                  required
                  className="input-field mt-1"
                  placeholder="Enter your club name"
                  value={clubName}
                  onChange={(e) => setClubName(e.target.value)}
                />
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input-field mt-1"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={6}
                className="input-field mt-1"
                placeholder="Enter your password (min. 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="flex items-start">
              <input
                id="acceptedTerms"
                name="acceptedTerms"
                type="checkbox"
                required
                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
              />
              <label htmlFor="acceptedTerms" className="ml-2 block text-sm text-gray-700">
                I accept the{' '}
                <a
                  href={`${marketingUrl}/terms`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-500 underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms and Conditions
                </a>
                {' '}and{' '}
                <a
                  href={`${marketingUrl}/privacy`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-500 underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
              </label>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full text-lg py-3"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/store" className="text-blue-600 hover:text-blue-500 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
